import { Injectable, Inject } from '@angular/core';
import { Error, REPORT_PARAM_TRANSFORMERS, ReportParamsTransformer } from './models';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import * as bowser from 'bowser';

const ISSUE_REPORT_EMAIL_ADDRESS = 'prodapps@rt.mtvi.com';
const browser = bowser.getParser(window.navigator.userAgent);
const osInfo = browser.getOS();
const platformInfo = browser.getPlatform();
const browserInfo = browser.getBrowser();

@Injectable()
export class IssueReportingService {
  private logger: LoggerService;

  constructor(
    loggerService: LoggerService,
    @Inject(REPORT_PARAM_TRANSFORMERS) private paramTransformers: ReportParamsTransformer[]
  ) {
    this.logger = loggerService.instance('IssueReportingService');
  }

  private getOSString(): string {
    if (!osInfo) {
      return this.getPlatformString();
    }
    return (
      `${osInfo.name} ${osInfo.version} ${osInfo.versionName ? osInfo.versionName + ' ' : ''}` +
      this.getPlatformString()
    );
  }

  private getPlatformString(): string {
    if (!platformInfo) {
      return navigator.platform;
    }

    return (
      `${platformInfo.vendor ? platformInfo.vendor + ' ' : ''}` +
      `${platformInfo.model ? platformInfo.model + ' ' : ''}` +
      `(${platformInfo.type})`
    );
  }

  private getBrowserString(): string {
    if (!browserInfo) {
      return navigator.appVersion;
    }

    return `${browserInfo.name} ${browserInfo.version}`;
  }

  private getErrorString(error?: Error): string {
    if (!error) {
      return undefined;
    }
    let errStr = error.message;

    if (error.error) {
      try {
        errStr += ' - ';
        errStr += JSON.stringify(error.error);
      } catch (jsonError) {
        // ignore
      }
    }
    return errStr;
  }

  getIssueDetails(error?: Error): { [key: string]: string } {
    let issueDetails = {
      Location: window.location.href,
      Version: this.logger.appVersion,
      Error: this.getErrorString(error),
      'Error Source': error ? error.source : undefined,
      'Operating System': this.getOSString(),
      'Browser Version': this.getBrowserString()
    };

    this.paramTransformers
      .filter(paramTransformer => paramTransformer.initialized)
      .forEach(transformer => {
        issueDetails = {
          ...issueDetails,
          ...transformer.getParams()
        };
      });

    return issueDetails;
  }

  private getBodyText(
    issueDetails: { [key: string]: string },
    filter?: string,
    includeFilter?: boolean
  ): string {
    const filterFunc = (key: string) => {
      if (!filter) {
        return true;
      }
      const hasFilter = key.toLowerCase().includes(filter);
      return includeFilter ? hasFilter : !hasFilter;
    };
    return Object.keys(issueDetails)
      .filter(filterFunc)
      .reduce((bodyStr, key) => {
        return bodyStr + (issueDetails[key] ? `${key}: ${issueDetails[key]}\n\n` : '');
      }, '');
  }

  private getMailLink(error?: Error): string {
    const issueDetails = this.getIssueDetails(error);
    const currentApp = (issueDetails as any).Application;

    const mailToUrl = new URL(`mailto:${ISSUE_REPORT_EMAIL_ADDRESS}`);
    mailToUrl.searchParams.set(
      'subject',
      decodeURIComponent(`Content Platform App Suite${currentApp ? ' - ' + currentApp : ''}`)
    );

    const bodyText =
      this.getBodyText(issueDetails, 'error', false) +
      `\nPlease describe your issue below in as much detail as you can along with any screenshots:\n\n\n\n` +
      this.getBodyText(issueDetails, 'error', true);

    mailToUrl.searchParams.set('body', decodeURIComponent(bodyText));
    return mailToUrl.toString().replace(new RegExp(/\+/, 'g'), ' ');
  }

  getReportIssueWithErrorMailLink(error: Error): string {
    return this.getMailLink(error);
  }

  getReportIssueMailLink(): string {
    return this.getMailLink();
  }

  reportIssue(): void {
    this.logger.warn('Reporting Issue');
    window.location.href = this.getReportIssueMailLink();
  }

  reportIssueWithError(error: Error): void {
    this.logger.warn('Reporting Issue with Error', { error: error.message });
    window.location.href = this.getReportIssueWithErrorMailLink(error);
  }
}
